<template>
  <DModal
    :showClose="false"
    :isBgOverlay="true"
    :isCloseOnBgClick="false"
    :appendToBody="true"
    :placeInTop="true"
    :autoFocusField="false"
    ariaLabel="Fravær i KMD Opus"
    @close="$emit('close')">
    <div class="d-modal text-dark" slot="insertComponent">
      <div class="d-modal__header">
        <div>
          <h2>Fravær i KMD Opus</h2>
          <p class="d-modal__person">
            <strong>{{ userName || 'Ukendt' }}</strong>
            <span v-if="employmentLabel" class="d-modal__person__employment">· {{ employmentLabel }}</span>
          </p>
          <p class="d-modal__subtitle">
            Viser fraværet, som det er registreret i KMD Opus lige nu. Data hentes direkte fra KMD, når vinduet åbnes, og når du klikker på Indlæs.
          </p>
        </div>
        <div class="font-large icon__bg-circle d-modal__header__close" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <div v-if="transfer" class="transfer-card">
        <div class="transfer-card__title">Valgt indberetning</div>
        <div class="transfer-card__grid">
          <div class="transfer-card__field">
            <span class="transfer-card__label">Fraværstype</span>
            <span>{{ transferTypeText || 'Ukendt' }}</span>
          </div>
          <div class="transfer-card__field">
            <span class="transfer-card__label">Startdato</span>
            <span>{{ transferStartLabel }}</span>
          </div>
          <div class="transfer-card__field">
            <span class="transfer-card__label">Slutdato</span>
            <span>{{ transferEndLabel }}</span>
          </div>
          <div class="transfer-card__field">
            <span class="transfer-card__label">Dage</span>
            <span>{{ formatDecimal(transfer.absence_days) }}</span>
          </div>
          <div class="transfer-card__field">
            <span class="transfer-card__label">Timer</span>
            <span>{{ formatDecimal(transfer.absence_hours) }}</span>
          </div>
          <div class="transfer-card__field">
            <span class="transfer-card__label">Status</span>
            <span>
              <span v-if="transfer.state === 'error'" class="status-pill status-pill--error">Fejlet</span>
              <span v-else-if="transfer.state === 'ok'" class="status-pill status-pill--ok">Gennemført</span>
              <span v-else class="status-pill status-pill--pending">Afventer</span>
            </span>
          </div>
        </div>
        <div v-if="transferMessages.length > 0" class="transfer-card__messages">
          <span class="transfer-card__label">Besked</span>
          <span v-for="(msg, idx) in transferMessages"
                :key="idx"
                :class="{'message-error': msg.StatusType === 'E', 'message-warning': msg.StatusType === 'W'}">
            {{ msg.MessageText }}
          </span>
        </div>
        <div v-if="matchStatus === 'match'" class="transfer-card__result transfer-card__result--match">
          {{ matchCount === 1 ? '1 tilsvarende post fundet i Opus' : matchCount + ' tilsvarende poster fundet i Opus' }}. Den er markeret med Match i tabellen.
        </div>
        <div v-else-if="matchStatus === 'other_type'" class="transfer-card__result transfer-card__result--warning">
          Ingen post med samme type, men {{ otherTypeOverlapCount }} {{ otherTypeOverlapCount === 1 ? 'overlappende post' : 'overlappende poster' }} med anden type: {{ otherTypeOverlapCodes }}
        </div>
        <div v-else-if="matchStatus === 'not_covered'" class="transfer-card__result transfer-card__result--warning">
          Den valgte periode dækker ikke indberetningens datoer. Vælg en periode, der dækker {{ transferStartLabel }} - {{ transferEndLabel }}, og klik på Indlæs.
        </div>
        <div v-else-if="matchStatus === 'no_match'" class="transfer-card__result transfer-card__result--none">
          Ingen tilsvarende post fundet i Opus
        </div>
        <div v-else-if="matchStatus === 'unknown_type'" class="transfer-card__result transfer-card__result--warning">
          Indberetningens fraværstype kendes ikke, så der kan ikke søges efter en tilsvarende post i Opus.
          <template v-if="otherTypeOverlapCount > 0">
            {{ otherTypeOverlapCount }} {{ otherTypeOverlapCount === 1 ? 'post' : 'poster' }} i Opus overlapper perioden: {{ otherTypeOverlapCodes }}
          </template>
        </div>
      </div>

      <div class="d-modal__toolbar">
        <div class="d-modal__toolbar__filters">
          <DoDatePickerRange
            :allowSameDate="true"
            :startDate="startDate"
            :endDate="endDate"
            :maximumDateRange="maximumDateRange"
            :dateShortCutsProps="dateShortCuts"
            @startChanged="startDate = $event"
            @endChanged="endDate = $event">
          </DoDatePickerRange>
          <button
            class="do-bttn do-bttn--submit"
            :class="{'do-bttn--loading': isLoading}"
            :disabled="isLoading"
            @click="readKmdAbsence">
            <span>Indlæs</span>
          </button>
          <span v-if="hasLoaded && !isLoading && isPeriodChanged" class="d-modal__hint">
            Perioden er ændret. Klik på Indlæs for at hente den fra KMD.
          </span>
        </div>
        <span v-if="hasLoaded && !isLoading && !errorMessage" class="d-modal__count">
          {{ rows.length }} {{ rows.length === 1 ? 'fraværsperiode' : 'fraværsperioder' }} · {{ loadedPeriodLabel }}
        </span>
      </div>

      <div class="d-modal__content">
        <div v-if="isLoading" class="d-modal__status">
          <Loader :isSmall="true"></Loader>
          <p class="d-modal__progress">Henter fravær fra KMD Opus…</p>
        </div>
        <div v-else-if="errorMessage" class="d-modal__error">
          {{ errorMessage }}
        </div>
        <p v-else-if="hasLoaded && rows.length === 0" class="d-modal__empty">
          Der er ikke registreret fravær i KMD Opus i perioden {{ loadedPeriodLabel }}.
        </p>
        <table v-else-if="hasLoaded" class="do-table">
          <thead>
          <tr>
            <th>Fraværstype</th>
            <th class="td-date">Startdato</th>
            <th class="td-date">Slutdato</th>
            <th v-if="hasTimes" class="td-time">Tid</th>
            <th class="is-number">Dage</th>
            <th class="is-number">Timer</th>
            <th class="is-number">Kalenderdage</th>
            <th class="td-origin">Oprettet i</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="row in rows" :key="row.key" :class="{'row--match': row.isMatch, 'row--overlap': row.isOtherTypeOverlap}">
            <td>
              {{ row.typeText || 'Ukendt fraværstype' }}
              <span v-if="row.typeCode" class="type-code">({{ row.typeCode }})</span>
              <span v-if="row.isMatch" class="match-badge">Match</span>
              <span v-if="row.isOtherTypeOverlap" class="overlap-badge">Overlapper, anden type</span>
            </td>
            <td class="td-date">{{ row.vStartDate }}</td>
            <td class="td-date">{{ row.vEndDate }}</td>
            <td v-if="hasTimes" class="td-time">{{ row.vTime }}</td>
            <td class="is-number">{{ formatDecimal(row.days) }}</td>
            <td class="is-number">{{ formatDecimal(row.hours) }}</td>
            <td class="is-number">{{ formatDecimal(row.calendarDays) }}</td>
            <td class="td-origin">
              <span v-if="row.isFromDocendo" class="status-pill status-pill--docendo">Oprettet i docendo</span>
              <span v-else class="status-pill status-pill--opus">Oprettet i Opus</span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </DModal>
</template>

<script>
import docendo from '../../../docendo'
import moment from 'moment'
import DModal from '../../common/DModal.vue'
import DoDatePickerRange from '../../datepicker/DoDatePickerRange.vue'
import Loader from '../../common/Loader.vue'

const GENERIC_ERROR = 'Kunne ikke hente fravær fra KMD Opus. Prøv igen eller kontakt support.'
const NOT_FOUND_ERROR = 'Ansættelsen findes ikke som aktiv Opus-ansættelse, så fraværet kan ikke hentes fra KMD Opus.'
const MAX_RANGE_DAYS = 731

function normalizeCode (value) {
  return value == null ? '' : String(value).trim().toLowerCase()
}

// Datoer som 'YYYY-MM-DD'; '' betyder åben (uendelig) i den ende
function periodsOverlap (aStart, aEnd, bStart, bEnd) {
  const startsBeforeEnd = aStart === '' || bEnd === '' || aStart <= bEnd
  const endsAfterStart = aEnd === '' || bStart === '' || aEnd >= bStart
  return startsBeforeEnd && endsAfterStart
}

function isOpenEndedTransfer (transfer) {
  return !!transfer.ongoing || !transfer.to_date || String(transfer.to_date).indexOf('9999') === 0
}

// Standardperiode som backend (i dag -1 md. til i dag +1 år), udvidet så den altid dækker indberetningens datoer
function defaultPeriod (transfer) {
  let start = moment().subtract(1, 'month').startOf('day')
  let end = moment().add(1, 'year').endOf('day')
  if (transfer && transfer.from_date && moment(transfer.from_date).isValid()) {
    const transferStart = moment(transfer.from_date).startOf('day')
    if (transferStart.isBefore(start)) start = transferStart
    if (!isOpenEndedTransfer(transfer) && moment(transfer.to_date).isValid()) {
      const transferEnd = moment(transfer.to_date).endOf('day')
      if (transferEnd.isAfter(end)) end = transferEnd
    }
  }
  if (end.diff(start, 'days') >= MAX_RANGE_DAYS) {
    end = moment(start).add(MAX_RANGE_DAYS - 1, 'days').endOf('day')
  }
  return {start, end}
}

export default {
  name: 'KmdOpusAbsenceModal',
  components: {DModal, DoDatePickerRange, Loader},
  props: {
    // id på skolens ansættelse (school_employment) med source opus
    employmentId: {
      type: [Number, String],
      required: true
    },
    userName: {
      type: String,
      default: ''
    },
    employmentLabel: {
      type: String,
      default: ''
    },
    // Hele rækken (indberetningen) fra Fraværsindberetninger-tabellen
    transfer: {
      type: Object,
      default: null
    },
    // Indberetningens KMD-fraværskode (kmd_absence_codes.absence_type, fx 'SY')
    absenceTypeCode: {
      type: String,
      default: ''
    }
  },
  data () {
    const period = defaultPeriod(this.transfer)
    return {
      startDate: period.start,
      endDate: period.end,
      maximumDateRange: MAX_RANGE_DAYS,
      kmdAbsence: [],
      isLoading: false,
      hasLoaded: false,
      errorMessage: '',
      loadedFromDate: '',
      loadedToDate: '',
      requestId: 0
    }
  },
  computed: {
    schoolId () {
      return this.$store.getters.schoolId
    },
    fromDate () {
      return moment(this.startDate).format('YYYY-MM-DD')
    },
    toDate () {
      return moment(this.endDate).format('YYYY-MM-DD')
    },
    isPeriodChanged () {
      return this.fromDate !== this.loadedFromDate || this.toDate !== this.loadedToDate
    },
    loadedPeriodLabel () {
      if (!this.loadedFromDate || !this.loadedToDate) return ''
      return this.formatDate(this.loadedFromDate) + ' - ' + this.formatDate(this.loadedToDate)
    },
    dateShortCuts () {
      const today = moment()
      return [
        {title: 'Standard (1 md. tilbage - 1 år frem)', startDate: moment(today).subtract(1, 'month').startOf('day'), endDate: moment(today).add(1, 'year').endOf('day')},
        {title: 'Denne måned', startDate: moment(today).startOf('month'), endDate: moment(today).endOf('month')},
        {title: 'Seneste 3 måneder', startDate: moment(today).subtract(3, 'months').startOf('day'), endDate: moment(today).endOf('day')},
        {title: 'Dette år', startDate: moment(today).startOf('year'), endDate: moment(today).endOf('year')}
      ]
    },
    rows () {
      const rows = this.kmdAbsence.map((item, idx) => {
        const start = this.parseDate(item.kmd_start_date)
        // Alle kmd_*-felter er strenge fra XML; manglende værdier er ''
        const endRaw = item.kmd_end_date || ''
        const isOpenEnded = endRaw.indexOf('9999') === 0
        const end = isOpenEnded ? null : this.parseDate(endRaw)
        const startTime = this.formatTime(item.kmd_start_time)
        const isOverlap = this.isTransferOverlap(start ? start.format('YYYY-MM-DD') : '', end ? end.format('YYYY-MM-DD') : '')
        const isMatch = isOverlap && this.isSameTypeAsTransfer(item.kmd_absence_attendance_type)
        return {
          key: [item.kmd_object_id, item.kmd_record_number, item.kmd_external_id, idx].join('-'),
          typeText: item.kmd_absence_attendance_type_text || '',
          typeCode: item.kmd_absence_attendance_type || '',
          vStartDate: start ? start.format('DD-MM-YYYY') : (item.kmd_start_date || ''),
          vEndDate: isOpenEnded ? 'Løbende' : (end ? end.format('DD-MM-YYYY') : endRaw),
          vTime: this.formatTimeRange(item.kmd_start_time, item.kmd_end_time),
          days: item.kmd_absence_attendance_days,
          hours: item.kmd_absence_attendance_hours,
          calendarDays: item.kmd_absence_attendance_calendar_days,
          // event_serial_id er null, når fraværet er oprettet direkte i Opus
          isFromDocendo: !!item.event_serial_id,
          // Grøn: samme type og overlap. Gul: overlap med anden type (eller ukendt type på indberetningen)
          isMatch: isMatch,
          isOtherTypeOverlap: isOverlap && !isMatch,
          sortKey: (start ? start.format('YYYY-MM-DD') : '0000-00-00') + ' ' + (startTime || '00:00')
        }
      })
      return rows.sort((a, b) => {
        if (a.sortKey === b.sortKey) return 0
        return a.sortKey < b.sortKey ? -1 : 1
      })
    },
    transferStartKey () {
      return this.transfer ? this.toDateKey(this.transfer.from_date) : ''
    },
    transferIsOpenEnded () {
      return this.transfer ? isOpenEndedTransfer(this.transfer) : false
    },
    transferEndKey () {
      return this.transfer && !this.transferIsOpenEnded ? this.toDateKey(this.transfer.to_date) : ''
    },
    transferTypeCode () {
      return normalizeCode(this.absenceTypeCode)
    },
    transferTypeText () {
      return this.transfer ? (this.transfer.vAbsenceTypeText || '') : ''
    },
    transferStartLabel () {
      return this.transfer ? (this.transfer.vStartDate || '') : ''
    },
    transferEndLabel () {
      if (!this.transfer) return ''
      return this.transfer.ongoing ? 'Åbenstående' : (this.transfer.vEndDate || '')
    },
    transferMessages () {
      const errors = this.transfer && this.transfer.error
      return Array.isArray(errors) ? errors.filter(msg => msg && msg.MessageText) : []
    },
    matchCount () {
      return this.rows.filter(row => row.isMatch).length
    },
    isLoadedPeriodCoveringTransfer () {
      if (!this.transferStartKey || !this.loadedFromDate || !this.loadedToDate) return true
      if (this.loadedFromDate > this.transferStartKey) return false
      if (this.transferIsOpenEnded || !this.transferEndKey) return this.loadedToDate >= this.transferStartKey
      return this.loadedToDate >= this.transferEndKey
    },
    otherTypeOverlapRows () {
      return this.rows.filter(row => row.isOtherTypeOverlap)
    },
    otherTypeOverlapCount () {
      return this.otherTypeOverlapRows.length
    },
    otherTypeOverlapCodes () {
      const seen = {}
      const codes = []
      this.otherTypeOverlapRows.forEach(row => {
        const code = row.typeCode ? String(row.typeCode).trim() : ''
        const key = normalizeCode(code)
        if (seen[key]) return
        seen[key] = true
        codes.push(code || 'ukendt kode')
      })
      return codes.join(', ')
    },
    matchStatus () {
      if (!this.transfer || this.isLoading || !this.hasLoaded || this.errorMessage) return ''
      if (!this.transferTypeCode) return 'unknown_type'
      if (this.matchCount > 0) return 'match'
      if (this.otherTypeOverlapCount > 0) return 'other_type'
      if (!this.isLoadedPeriodCoveringTransfer) return 'not_covered'
      return 'no_match'
    },
    hasTimes () {
      return this.rows.some(row => !!row.vTime)
    }
  },
  methods: {
    // Hvert kald går live til KMD, så der hentes kun ved åbning og ved klik på Indlæs
    readKmdAbsence () {
      if (this.isLoading) return
      this.requestId += 1
      const requestId = this.requestId
      const fromDate = this.fromDate
      const toDate = this.toDate
      this.isLoading = true
      this.errorMessage = ''
      // docendo.js bruger websocket-rails (/websocket): success-callback får event.data,
      // dvs. selve listen; failure-callback får {error, backtrace}
      docendo.listKmdOpusAbsenceForEmployment(this.schoolId, this.employmentId, fromDate, toDate, list => {
        if (requestId !== this.requestId) return
        this.loadedFromDate = fromDate
        this.loadedToDate = toDate
        if (Array.isArray(list)) {
          this.kmdAbsence = list
          this.errorMessage = ''
        } else {
          console.error('api.kmd_absence.index: uventet svar', list)
          this.kmdAbsence = []
          this.errorMessage = GENERIC_ERROR
        }
        this.isLoading = false
        this.hasLoaded = true
      }, failure => {
        if (requestId !== this.requestId) return
        const rawError = failure && failure.error ? String(failure.error) : ''
        // Rå fejl (fx Faraday-fejl med KMD-URL) logges kun i konsollen, ikke til brugeren
        console.error('api.kmd_absence.index fejlede:', rawError || failure)
        this.loadedFromDate = fromDate
        this.loadedToDate = toDate
        this.kmdAbsence = []
        this.errorMessage = /RecordNotFound|Couldn't find/i.test(rawError) ? NOT_FOUND_ERROR : GENERIC_ERROR
        this.isLoading = false
        this.hasLoaded = true
      })
    },
    isTransferOverlap (startKey, endKey) {
      if (!this.transfer) return false
      return periodsOverlap(startKey, endKey, this.transferStartKey, this.transferEndKey)
    },
    // Samme fraværskode, trimmet og uden forskel på store/små bogstaver (SAP kan polstre med mellemrum)
    isSameTypeAsTransfer (kmdTypeCode) {
      if (!this.transferTypeCode) return false
      return normalizeCode(kmdTypeCode) === this.transferTypeCode
    },
    toDateKey (value) {
      if (!value) return ''
      const date = moment(value)
      return date.isValid() ? date.format('YYYY-MM-DD') : ''
    },
    parseDate (value) {
      const str = value == null ? '' : String(value).trim()
      if (str === '') return null
      const date = moment(str, ['YYYY-MM-DD', 'YYYYMMDD', moment.ISO_8601], true)
      return date.isValid() ? date : null
    },
    formatDate (value) {
      const date = this.parseDate(value)
      return date ? date.format('DD-MM-YYYY') : (value || '')
    },
    formatTime (value) {
      const str = value == null ? '' : String(value).trim()
      if (str === '') return ''
      const time = moment(str, ['HH:mm:ss', 'HH:mm', 'HHmmss'], true)
      return time.isValid() ? time.format('HH:mm') : str
    },
    formatTimeRange (startTime, endTime) {
      const start = this.formatTime(startTime)
      const end = this.formatTime(endTime)
      if ((!start || start === '00:00') && (!end || end === '00:00')) return ''
      if (start && end) return start + ' - ' + end
      return start || end
    },
    formatDecimal (value) {
      // KMD sender tal som strenge ('' når værdien mangler), med '.' eller ',' som decimaltegn
      const str = value == null ? '' : String(value).trim()
      if (str === '') return ''
      const num = Number(str.replace(',', '.'))
      if (isNaN(num)) return str
      const formatted = num % 1 === 0 ? String(num) : num.toFixed(2)
      return formatted.replace('.', ',')
    }
  },
  mounted () {
    this.readKmdAbsence()
  },
  beforeDestroy () {
    // Ignorer svar, der ankommer efter vinduet er lukket
    this.requestId += 1
  }
}
</script>

<style scoped>
.d-modal {
  width: 1000px;
  max-width: calc(100vw - 32px);
  border-radius: 16px;
  position: relative;
  background: #fff;
}

.d-modal__header {
  margin-bottom: 0;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.d-modal__header h2 {
  margin: 0;
}

.d-modal__person {
  margin: 0.25rem 0 0;
  font-size: 14px;
}

.d-modal__person__employment {
  color: #6b7280;
}

.d-modal__subtitle {
  margin: 0.25rem 0 0;
  font-size: 13px;
  color: #6b7280;
  max-width: 52rem;
  line-height: 1.45;
}

.d-modal__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eee;
  flex-wrap: wrap;
}

.d-modal__toolbar__filters {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  min-width: 0;
}

.d-modal__hint,
.d-modal__count {
  color: #6b7280;
  font-size: 13px;
}

.d-modal__hint {
  font-style: italic;
}

.d-modal__content {
  padding: 1rem;
  max-height: calc(100vh - 260px);
  overflow: auto;
  overscroll-behavior: contain;
}

.d-modal__status {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
  gap: 0.75rem;
}

.d-modal__progress,
.d-modal__empty {
  margin: 0;
  color: #71717a;
}

.d-modal__empty {
  font-style: italic;
  padding: 1.5rem 0;
  text-align: center;
}

.d-modal__error {
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  padding: 0.75rem 1rem;
}

.do-table {
  width: 100%;
  border-collapse: collapse;
}

.do-table thead th {
  text-transform: uppercase;
  font-size: 10px;
  color: #8898aa;
  text-align: left;
  padding: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f6f9fc;
  white-space: nowrap;
}

.do-table tbody td {
  padding: 0.45rem 0.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.do-table tbody tr:nth-child(even) {
  background-color: #f9fafb;
}

.do-table thead th.is-number,
.is-number {
  text-align: right;
}

.td-date {
  width: 100px;
  white-space: nowrap;
}

.td-time {
  width: 110px;
  white-space: nowrap;
}

.td-origin {
  width: 150px;
  white-space: nowrap;
}

.type-code {
  color: #6b7280;
  font-size: 12px;
}

.status-pill {
  display: inline-block;
  padding: 2px 8px;
  line-height: 1.5;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-pill--docendo {
  background-color: #dbeafe;
  color: #1e40af;
}

.status-pill--opus {
  background-color: #f3f4f6;
  color: #4b5563;
}

.status-pill--ok {
  background-color: #dcfce7;
  color: #166534;
}

.status-pill--error {
  background-color: #fee2e2;
  color: #dc2626;
}

.status-pill--pending {
  background-color: #f3f4f6;
  color: #6b7280;
}

.transfer-card {
  margin: 0.75rem 1rem 0;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f6f9fc;
}

.transfer-card__title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #8898aa;
  margin-bottom: 0.5rem;
}

.transfer-card__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
}

.transfer-card__field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 80px;
}

.transfer-card__label {
  font-size: 10px;
  text-transform: uppercase;
  color: #8898aa;
}

.transfer-card__messages {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 0.5rem;
  font-size: 0.875rem;
}

.message-error {
  color: #dc2626;
  font-style: italic;
}

.message-warning {
  color: #222222;
  font-style: italic;
}

.transfer-card__result {
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
}

.transfer-card__result--match {
  background: #dcfce7;
  color: #166534;
}

.transfer-card__result--none {
  background: #fee2e2;
  color: #b91c1c;
}

.transfer-card__result--warning {
  background: #fef9c3;
  color: #854d0e;
}

.do-table tbody tr.row--match,
.do-table tbody tr.row--match:nth-child(even) {
  background-color: #ecfdf5;
  box-shadow: inset 3px 0 0 #16a34a;
}

.do-table tbody tr.row--overlap,
.do-table tbody tr.row--overlap:nth-child(even) {
  background-color: #fefce8;
  box-shadow: inset 3px 0 0 #eab308;
}

.overlap-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  background: #fef08a;
  color: #854d0e;
}

.match-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  background: #16a34a;
  color: #fff;
}
</style>



// WEBPACK FOOTER //
// KmdOpusAbsenceModal.vue?7c645168